/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

import { useState } from "react";
import { IconEdit } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";

import CreateClientModal from "./CreateClientModal";
import ClientRoleAccessSection from "./ClientAccessSection";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { T, useLanguage } from "../../../context/LanguageContext";

import {
  useMerchantListData,
  useMerchantAccessCreateMutation,
  useMerchantAccessByIdData,
} from "../../../apis/merchantApis";

export default function AccessManagement() {
  const { t } = useLanguage();
  const { allowed: canUpdateAccessCenter } = usePermission("access_center", "update");
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<"view" | "edit">("view");

  const { data: clients } = useMerchantListData(1, 100, "");
  const { refetch } = useMerchantAccessByIdData(selectedClient?.id);
  const { mutate: createAccess, isPending } = useMerchantAccessCreateMutation(
    selectedClient?.id || "",
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const openView = (client: any) => {
    setSelectedClient(client);
    setMode("view");
    setModalOpen(true);
    reset(client);
  };

  const openEdit = () => {
    setMode("edit");
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedClient(null);
    setMode("view");
    reset();
  };

  const handleUpdate = (formData: any) => {
    if (!formData.permissions || formData.permissions.length === 0) {
      toast.error(t("Please select at least one permission"));
      return;
    }

    createAccess(
      {
        data: formData.permissions,
      },
      {
        onSuccess: () => {
          toast.success(t("Access updated successfully"));
          setMode("view");
          setModalOpen(false);
          // Refetch client permissions immediately
          refetch();
        },
        onError: (err: any) => {
          toast.error(t(err?.message || "Something went wrong"));
        },
      },
    );
  };

  return (
    <div className="grid grid-cols-12 bg-white h-auto lg:h-[calc(100vh-10rem)]">
      <div className="col-span-12 lg:col-span-4 xl:col-span-3 border-r border-[#000000]/10 overflow-y-auto">
        <div className="flex items-center justify-between py-2 px-4 border-b border-[#000000]/10 h-16 sticky top-0   z-10">
          <h2 className="font-medium text-[#212121] text-lg"><T>Clients</T></h2>
        </div>

        <ul className="divide-y">
          {clients?.clients?.map((client: any) => (
            <li
              key={client.id}
              onClick={() => openView(client)}
              className={`flex items-center justify-between px-4 py-3 cursor-pointer ${
                selectedClient?.id === client.id
                  ? "bg-[#FBD037]"
                  : "hover:bg-black/5"
              }`}
            >
              {/* LEFT: Client Info */}
              <div className="flex items-center gap-3">
                <img
                  src={client.companyLogo || "/img/dummy-blank-profile.png"}
                  className="w-10 h-10 rounded-full border object-cover"
                  alt={client.name}
                />
                <span className="text-base font-medium text-[#212121]">
                  {client.name}
                </span>
              </div>

              {/* RIGHT: Edit Button */}
              {canUpdateAccessCenter && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // 🔥 VERY IMPORTANT
                    setSelectedClient(client);
                    setMode("edit");
                    setModalOpen(true);
                    reset(client);
                  }}
                  className="p-2 rounded-full border bg-[#F7F6F2] hover:bg-black/5"
                  title={t("Edit Client Permissions")}
                >
                  <IconEdit size={18} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* RIGHT PANEL – ACCESS */}
      <div className="col-span-12 lg:col-span-8 xl:col-span-9 h-full">
        {!modalOpen ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-3">
              <img src="/img/scan-data.png" alt="" />
              <h2 className="text-lg font-medium text-[#212121]">
                <T>Please Select Client</T>
              </h2>
              <p className="text-sm text-[#212121]/70">
                <T>Select a client to manage access</T>
              </p>
            </div>
          </div>
        ) : (
          <ClientRoleAccessSection
            title={
              mode === "view" ? "View Client Access" : "Edit Client Access"
            }
            open={modalOpen}
            actionButtonText={mode === "view" ? false : t("Update Access")}
            actionButtonOnClick={handleSubmit(handleUpdate)}
            actionButtonProps={{
              disabled: isPending,
              className: "!bg-blue-600 hover:!bg-blue-700",
            }}
            register={register}
            errors={errors}
            setValue={setValue}
            defaultValues={selectedClient}
            readOnly={mode === "view"}
            onEdit={canUpdateAccessCenter ? () => setMode("edit") : undefined}
          >
            <CreateClientModal
              register={register}
              errors={errors}
              setValue={setValue}
              defaultValues={selectedClient}
              readOnly={mode === "view"}
            />
          </ClientRoleAccessSection>
        )}
      </div>
    </div>
  );
}
